import {
  MapPinned,
  PanelsTopLeft,
  ShoppingCart,
  LayoutTemplate,
  LandmarkIcon,
  AudioWaveform,
  WaypointsIcon,
  LucideIcon,
  PaletteIcon,
  BarChartIcon,
  BrainIcon,
  CloudSunIcon,
  ChartCandlestickIcon,
  HeartPulseIcon,
  Rows3Icon,
  EarthIcon,
  CreditCardIcon,
  TruckIcon,
  SearchIcon,
} from 'lucide-react'

type Prompt = {
  name: string
  prompt: () => string
  icon: LucideIcon
}

const boldCommercePrompt: Prompt = {
  name: 'BOLD Commerce Bundle Builder',
  prompt:
    () => `Magic Spoon (https://www.magicspoon.com) wants a fully custom subscription bundle builder to let customers drag-and-drop different flavors into a multi-pack and subscribe. Build a PoC bundle interface integrated with BOLD Subscriptions and custom Shopify Liquid.

Deliverables
1. Create bundle product with 4 “slots” (e.g., 4-pack).
2. Drag-and-drop UI using React + Tailwind (or Shopify Section block if required).
3. As customer drops SKUs into slots, real-time cart state updates with variant IDs.
4. Bold Subscription API wraps the bundle in a recurring plan (monthly, bimonthly).
5. Order metadata captures selected flavors + slot position in case of fulfillment dependency.

Frontend
• \`/subscribe/builder\` page
• Bold Checkout embedded
• shadcn/ui cards used for flavor thumbnails and drop targets
• generate images shoqcasing product only

Constraints
• Cart assembly completes < 1s on drag event.
• Subscription checkout flow must carry correct product structure.
• Flavor mix metadata available in admin order view.

The flow should be User drags 4 flavors → builds bundle → completes subscription checkout → order metadata shows selected SKUs in correct order. Subscription activates`,
  icon: ShoppingCart,
}

const boltFoodDeliveryPrompt: Prompt = {
  name: 'Food Delivery Platform',
  prompt:
    () => `Build a delivery platform that looks like Bolt Food (https://bolt.eu/pl-pl/food/). We want the same branding.
It should displays a list of restaurant with a type of cuisine, rating, distance, delivery fees.
Each restaurant has its own page with a menu. Each item in the menu needs a price, title, description and picture.
The user can add items to the cart and pass the order to the restaurant.

I live in Cracow, Poland.

We want to store all the data (restaurants, menu, clients, orders, etc.) in PostgreSQL.
There should be at least 20 restaurant and each restaurant should have at least 10 menu items. The restaurant should have a real address in Cracow.
We should see on the main page a map (OpenStreetmap) with pins to locate the available restaurant. When we click on the pin, we should be redirected to the restaurant's page.`,
  icon: TruckIcon,
}

const elevenLabsNytPrompt: Prompt = {
  name: 'Elevenlabs Narrated Articles',
  prompt:
    () => `Build a complete proof of concept for The New York Times. Handle everything: installing dependencies, writing code, fetching/generating content, and generating audio.

Create at least 3 sample articles with title, byline, image, and full text. Generate high-quality text-to-speech audio for each article using the ElevenLabs API. The player should be a sticky player at the bottom of the page. It must have a functional and visible play/pause icon on the left side of the duration bar.

Use a consistent stock voice. Do this at build time, not dynamically.

Save the audio as public/audio/<article>.mp3. On each article page, show a "Listen" button. Clicking it must play the corresponding audio using a native HTML element.

Deliver a polished, working solution where every "Listen" button plays the correct pre-generated audio without errors.`,
  icon: AudioWaveform,
}

const canvaInteractivePlaygroundPrompt: Prompt = {
  name: 'Theme Builder',
  prompt:
    () => `Canva (https://www.canva.com) needs a tool to let users define and preview themes for their brand kits. Deliver a theme builder with live preview, color contrast checks, and an option to export the generated theme as JSON.

Deliverables
1. \`/theme-builder\`: Palette input (primary, secondary, neutral)
2. Contrast checkers and WCAG validation
3. Typography selector w/ Google Fonts integration
4. Live preview iframe styled using current theme tokens
5. JSON export button: download design tokens in standard schema

Frontend
• Interactive controls for theme tokens
• Live preview
• Contrast ratio calculator

Constraints
• JSON tokens must match design system schema

The flow must be User defines theme → preview renders instantly → contrast passes WCAG → tokens exported to download → clipboard copy available.`,
  icon: PaletteIcon,
}

const shopwareAcana: Prompt = {
  name: 'Shopware Ecommerce for Pets',
  prompt: () => `Let's build a POC for a new prospect, Acana, using Shopware.

Their website is https://www.acana.com

1. Start a new Shopware instance 
2. Read all the existing content 
3. Adjust the branding to match prospect branding (theme colors, images, logo) 
4. Create 2 categories of products. The categories must have a description. 
5. For each category add 3 products. The products must have description and an image. 
6. Generate and put a nice hero image on the storefront's main page.`,
  icon: ShoppingCart,
}

const shopwareIkea: Prompt = {
  name: 'Shopware Ecommerce for Furniture',
  prompt: () => `Let's build a POC for a new prospect, IKEA, using Shopware.

Their website is https://www.ikea.com

1. Start a new Shopware instance 
2. Read all the existing content 
3. Adjust the branding to match prospect branding (theme colors, images, logo) 
4. Create 2 categories of products. The categories must have a description. 
5. For each category add 3 products. The products must have description and an image.`,
  icon: ShoppingCart,
}

const streamlitDoordash: Prompt = {
  name: 'Streamlit Dashboard',
  prompt:
    () => `DoorDash (https://www.doordash.com) wants a regional driver performance tool for internal ops leads. Deliver a prototype app showing heatmaps of delivery efficiency and historical drop-off metrics.

Deliverables  
1. Generate mock driver metrics (\`driver_id\`, \`delivery_time\`, \`drop_rate\`, \`region\`, \`lat/lon\`).  
2. Sidebar filters:  
   - Region  
   - Time range  
   - Driver status (active, inactive)  
3. \`st.map()\` + heatmap overlays by region  
4. Efficiency trend line (time series)  
5. Downloadable report summary as PDF or CSV

Frontend  
• Uses \`st.tabs()\` for Maps vs Time Series view   
• Color scale toggle (absolute vs normalized)`,
  icon: BarChartIcon,
}

const laneCrawfordAndStripe: Prompt = {
  name: 'Next.js Ecommerce with Stripe',
  prompt:
    () => `We want to create an ecommerce shop's demonstration for our prospect, Lane Crawford. Their website is https://www.lanecrawford.com/

We want:
1. the main page should display some marketing content
2. a page with the list of categories with at least two categories of products
3. for each category, a page with at least 3 products per categories
4. a product detail page with several variant and options for each product
5. a cart mechanism, with a checkout page (we need customer address and delivery options)
6. checkout implemented with Stripe

The whole website and content should be branded like the prospect.`,
  icon: CreditCardIcon,
}

const duolingoQuiz: Prompt = {
  name: 'Language Quiz',
  prompt:
    () => `Duolingo (https://www.duolingo.com) wants a modular, animated quiz engine that can be reused across lessons. Deliver a gamified system with streaks, XP rewards, and progress feedback.

  Deliverables

  1. \`<QuizEngine>\` component with question/answer logic
  2. XP bar and streak tracker
  3. Animated feedback: confetti, audio, color transitions
  4. Navigation controls: skip, back, retry
  5. Final result screen with shareable badge + leaderboard CTA
  6. Generate sample lessions centered around learning new languages.

  Frontend

  - Tailwind + Framer Motion for animations
  - Audio cue on correct/incorrect
  - Confetti explosion on 100% or new streak milestone

  Constraints

  - XP + streak logic must be persisted locally.

  The flow must be User completes quiz → XP applied → animation plays → streak updated → shareable badge shown. All UI transitions validated.`,
  icon: EarthIcon,
}

const inpostFinder: Prompt = {
  name: 'Drop-off Points Localizer',
  prompt:
    () => `Build a React + Next.js parcel locker finder app for InPost (https://inpost.pl/en/find-location).

Scope & Goal:  
Create a location finder where users can search for nearby parcel lockers, view details, and filter by services offered.

Deliverables:  
1. Search input with autocomplete for city, street, or postal code.  
2. Map component showing locker locations with markers (use Leaflet or Mapbox GL).  
3. Filter panel for locker features (e.g., 24/7 access, parcel size limits, return service).  
4. Locker details popup with address, operating hours, and available services.  
5. Responsive layout with Tailwind CSS; mobile-first design.

Features:  
- Geolocation API to center map on user location if permitted.  
- Lazy loading of lockers as user pans or zooms map.  
- Keyboard accessibility for search and filters.  
- Smooth marker animations on load and filter change.

✓ User searches “Warsaw” → map centers → parcel lockers appear → user filters by 24/7 access → locker details display on click`,
  icon: MapPinned,
}

const typeformBuilder = {
  name: 'No-code Form Builder',
  prompt:
    () => `Build a Next.js Website for Typeform – “Headless Form Builder with Live Preview and Submission Tracking”

Background & Goal:

Typeform wants to explore a headless form-builder proof of concept that separates the authoring UI from the form runtime. Your task is to deliver a functional Next.js app that lets users drag-and-drop fields, preview the form live, and capture submissions.

⸻

Core Deliverables:
0. Beautiful Typeform landing page with a hero section, CTA button redirecting to the Form Builder, key benefits section, social proof, and etc. I want this page to be beautiful, reflecting Typeform style.

1. Form Builder – /form/builder
• Layout
• Left column – Field palette (Text, Email, Rating, etc. - I want to have at least 10 various types of fields).
• Main canvas – Drag fields onto the layout and reorder via drag-and-drop
• Right panel – Field-specific properties using shadcn <Select> and <Switch> controls.
• Essential Features
• Working drag-and-drop
• “Save & Preview” button: persists the current JSON to localStorage and Postgres, then navigates to /form/preview.
• Re-opening /form/builder restores the last saved structure.

2. Live Preview – /form/preview
• Renders the form directly from the saved JSON with zero extra configuration.

3. Submission Endpoint – /form/submit
• Uses Next.js Server Actions to write answers to Postgres.
• Returns success state for UX feedback.

⸻

Logo Usage Use only the standalone Typeform logo asset—no extra text duplicates

⸻

Acceptance Criteria
1. User can build a form via drag-and-drop on desktop.
2. Clicking Save & Preview shows an accurate live preview of the saved JSON at /form/preview.
3. Returning to /form/builder reloads the previously saved form.
4. Submitting the form posts data to Postgres.
5. The UI is accessible (WCAG AA) and visually polished (soft cards, rounded-2xl, adequate padding).`,
  icon: Rows3Icon,
}

const cvsClassifier: Prompt = {
  name: 'Heart Disease Classifier',
  prompt: () => `We're going to build a "heart disease classifier" for https://www.cvs.com/.

I need a nice UI that takes in the following values, it should default to some nice values so that people don't have to input everything all at once. Then it should say percentage wise if someone should go talk to their doctor about heart disease now, and provide some follow up info about what they can do about heart disease.

The info we need to take in is:
age
sex
chest pain type (4 values 0, 1, 2, 3)
resting blood pressure
serum cholestoral in mg/dl
fasting blood sugar > 120 mg/dl
resting electrocardiographic results (values 0,1,2)
maximum heart rate achieved
exercise induced angina
oldpeak = ST depression induced by exercise relative to rest
the slope of the peak exercise ST segment
number of major vessels (0-3) colored by flourosopy
thal: 0 = normal; 1 = fixed defect; 2 = reversable defect`,
  icon: HeartPulseIcon,
}

const weatherDashboard: Prompt = {
  name: 'Snowflake Weather Dashboard',
  prompt: () =>
    `Build me a nice Weather.com dashboard for my data in Snowflake in React but start with mock data. Make components for wind speed, humidity and temperature displayed on a graph.`,
  icon: CloudSunIcon,
}

const chipotlePlatform: Prompt = {
  name: 'Burrito Trading Platform',
  prompt: () =>
    `Create a playful Chipotle-themed trading desk with a ticker strip, order-book table, and trade ticket panel for burrito-backed CDOs and swaps. Show live-updating price charts, risk dials, and a leaderboard of top long and short positions, all driven by mocked data. Let users place simulated buy, sell, and swap orders and instantly see position summaries—no sign-in, payments, or real settlement.`,
  icon: ChartCandlestickIcon,
}

const allbirdsSearch: Prompt = {
  name: 'Ecommerce Listing with Algolia',
  prompt:
    () => `Allbirds (https://www.allbirds.com) wants to boost sustainable products in search results and allow filtering based on environmental attributes. Build a PoC index with ranking and facet control using Algolia.

Deliverables:
1. Algolia index must contain sustainability metadata per product (is the product sustainable: \`is_sustainable\`, what's the carbon footprint: \`carbon_score\`).
2. Custom ranking formula: boost sustainable products.
3. Faceted filters: eco-friendly, low-carbon, recycled materials.

Come up with the products matching Allbirds branding and insert them into Algolia. Use index called \`allbirds_search\`. Then proceed with integration of that index into the app.

User flow: searching for keyword "runners" → I can see products matching the filter. There's a filter panel where I can choose e.g. eco-friendly products.`,
  icon: SearchIcon,
}

export const prompts = [
  boldCommercePrompt,
  elevenLabsNytPrompt,
  shopwareAcana,
  canvaInteractivePlaygroundPrompt,
  boltFoodDeliveryPrompt,
  shopwareIkea,
  streamlitDoordash,
  laneCrawfordAndStripe,
  duolingoQuiz,
  inpostFinder,
  typeformBuilder,
  cvsClassifier,
  weatherDashboard,
  chipotlePlatform,
  allbirdsSearch,
]

function getLandingPagePrompt() {
  const { name, url } = rollProspect()

  return `Build a Marketing Landing Page for my prospect ${name} (${url}). Use Shadcn, and a NextJS. It should have a nice hero image at the top taking up the full width. Underneath the hero image it should contain nicely designed images in a flex grid layout where its alternating pictures of products with slogans.`
}

function rollProspect() {
  const prospects = [
    {
      name: 'Nike',
      url: 'www.nike.com',
      category: 'Fashion and Lifestyle',
    },
    {
      name: 'Adidas',
      url: 'www.adidas.com',
      category: 'Fashion and Lifestyle',
    },
    {
      name: 'Stussy',
      url: 'www.stussy.com',
      category: 'Fashion and Lifestyle',
    },
    {
      name: 'Supreme',
      url: 'www.supremenewyork.com',
      category: 'Fashion and Lifestyle',
    },
    {
      name: 'Uniqlo',
      url: 'www.uniqlo.com',
      category: 'Fashion and Lifestyle',
    },
    {
      name: 'Zara',
      url: 'www.zara.com',
      category: 'Fashion and Lifestyle',
    },
    {
      name: 'H&M',
      url: 'www.hm.com',
      category: 'Fashion and Lifestyle',
    },
    {
      name: 'House of Hackney',
      url: 'www.houseofhackney.com',
      category: 'Fashion and Lifestyle',
    },
    {
      name: 'Gucci',
      url: 'www.gucci.com',
      category: 'Fashion and Lifestyle',
    },
    {
      name: 'Louis Vuitton',
      url: 'www.louisvuitton.com',
      category: 'Fashion and Lifestyle',
    },
    {
      name: 'The North Face',
      url: 'www.thenorthface.com',
      category: 'Fashion and Lifestyle',
    },
    {
      name: 'Patagonia',
      url: 'www.patagonia.com',
      category: 'Fashion and Lifestyle',
    },
    {
      name: 'Urban Outfitters',
      url: 'www.urbanoutfitters.com',
      category: 'Fashion and Lifestyle',
    },
    {
      name: 'Anthropologie',
      url: 'www.anthropologie.com',
      category: 'Fashion and Lifestyle',
    },
    {
      name: 'ASOS',
      url: 'www.asos.com',
      category: 'Fashion and Lifestyle',
    },
    {
      name: 'Aldi',
      url: 'www.aldi.com',
      category: 'Supermarkets and Retail Chains',
    },
    {
      name: 'Lidl',
      url: 'www.lidl.com',
      category: 'Supermarkets and Retail Chains',
    },
    {
      name: 'Tesco',
      url: 'www.tesco.com',
      category: 'Supermarkets and Retail Chains',
    },
    {
      name: 'Walmart',
      url: 'www.walmart.com',
      category: 'Supermarkets and Retail Chains',
    },
    {
      name: 'Target',
      url: 'www.target.com',
      category: 'Supermarkets and Retail Chains',
    },
    {
      name: 'Carrefour',
      url: 'www.carrefour.com',
      category: 'Supermarkets and Retail Chains',
    },
    {
      name: 'Costco',
      url: 'www.costco.com',
      category: 'Supermarkets and Retail Chains',
    },
    {
      name: 'Kroger',
      url: 'www.kroger.com',
      category: 'Supermarkets and Retail Chains',
    },
    {
      name: "Sainsbury's",
      url: 'www.sainsburys.co.uk',
      category: 'Supermarkets and Retail Chains',
    },
    {
      name: 'Whole Foods',
      url: 'www.wholefoodsmarket.com',
      category: 'Supermarkets and Retail Chains',
    },
    {
      name: 'Media Markt',
      url: 'www.mediamarkt.de',
      category: 'Electronics and Technology',
    },
    {
      name: 'Best Buy',
      url: 'www.bestbuy.com',
      category: 'Electronics and Technology',
    },
    {
      name: 'Apple',
      url: 'www.apple.com',
      category: 'Electronics and Technology',
    },
    {
      name: 'Samsung',
      url: 'www.samsung.com',
      category: 'Electronics and Technology',
    },
    {
      name: 'Sony',
      url: 'www.sony.com',
      category: 'Electronics and Technology',
    },
    {
      name: 'Microsoft',
      url: 'www.microsoft.com',
      category: 'Electronics and Technology',
    },
    {
      name: 'Dell',
      url: 'www.dell.com',
      category: 'Electronics and Technology',
    },
    {
      name: 'HP',
      url: 'www.hp.com',
      category: 'Electronics and Technology',
    },
    {
      name: 'LG',
      url: 'www.lg.com',
      category: 'Electronics and Technology',
    },
    {
      name: 'Lenovo',
      url: 'www.lenovo.com',
      category: 'Electronics and Technology',
    },
    {
      name: 'John Deere',
      url: 'www.deere.com',
      category: 'Agriculture and Equipment',
    },
    {
      name: 'Caterpillar',
      url: 'www.cat.com',
      category: 'Agriculture and Equipment',
    },
    {
      name: 'Kubota',
      url: 'www.kubota.com',
      category: 'Agriculture and Equipment',
    },
    {
      name: 'Case IH',
      url: 'www.caseih.com',
      category: 'Agriculture and Equipment',
    },
    {
      name: 'New Holland',
      url: 'www.newholland.com',
      category: 'Agriculture and Equipment',
    },
    {
      name: 'Massey Ferguson',
      url: 'www.masseyferguson.com',
      category: 'Agriculture and Equipment',
    },
    {
      name: 'CLAAS',
      url: 'www.claas.com',
      category: 'Agriculture and Equipment',
    },
    {
      name: 'AGCO',
      url: 'www.agco.com',
      category: 'Agriculture and Equipment',
    },
    {
      name: 'Fendt',
      url: 'www.fendt.com',
      category: 'Agriculture and Equipment',
    },
    {
      name: 'Vermeer',
      url: 'www.vermeer.com',
      category: 'Agriculture and Equipment',
    },
    {
      name: 'IKEA',
      url: 'www.ikea.com',
      category: 'Home and Furniture',
    },
    {
      name: 'Wayfair',
      url: 'www.wayfair.com',
      category: 'Home and Furniture',
    },
    {
      name: 'Crate & Barrel',
      url: 'www.crateandbarrel.com',
      category: 'Home and Furniture',
    },
    {
      name: 'West Elm',
      url: 'www.westelm.com',
      category: 'Home and Furniture',
    },
    {
      name: 'Pottery Barn',
      url: 'www.potterybarn.com',
      category: 'Home and Furniture',
    },
    {
      name: 'Restoration Hardware',
      url: 'www.rh.com',
      category: 'Home and Furniture',
    },
    {
      name: 'CB2',
      url: 'www.cb2.com',
      category: 'Home and Furniture',
    },
    {
      name: 'Article',
      url: 'www.article.com',
      category: 'Home and Furniture',
    },
    {
      name: 'HomeGoods',
      url: 'www.homegoods.com',
      category: 'Home and Furniture',
    },
    {
      name: 'Ethan Allen',
      url: 'www.ethanallen.com',
      category: 'Home and Furniture',
    },
    {
      name: "Dick's Sporting Goods",
      url: 'www.dickssportinggoods.com',
      category: 'Sporting Goods',
    },
    {
      name: 'REI',
      url: 'www.rei.com',
      category: 'Sporting Goods',
    },
    {
      name: 'Decathlon',
      url: 'www.decathlon.com',
      category: 'Sporting Goods',
    },
    {
      name: 'Under Armour',
      url: 'www.underarmour.com',
      category: 'Sporting Goods',
    },
    {
      name: 'Puma',
      url: 'www.puma.com',
      category: 'Sporting Goods',
    },
    {
      name: 'New Balance',
      url: 'www.newbalance.com',
      category: 'Sporting Goods',
    },
    {
      name: 'Columbia',
      url: 'www.columbia.com',
      category: 'Sporting Goods',
    },
    {
      name: 'Asics',
      url: 'www.asics.com',
      category: 'Sporting Goods',
    },
    {
      name: 'Fila',
      url: 'www.fila.com',
      category: 'Sporting Goods',
    },
    {
      name: 'Reebok',
      url: 'www.reebok.com',
      category: 'Sporting Goods',
    },
    {
      name: 'Sephora',
      url: 'www.sephora.com',
      category: 'Beauty and Personal Care',
    },
    {
      name: 'Ulta',
      url: 'www.ulta.com',
      category: 'Beauty and Personal Care',
    },
    {
      name: 'MAC Cosmetics',
      url: 'www.maccosmetics.com',
      category: 'Beauty and Personal Care',
    },
    {
      name: "L'Oréal",
      url: 'www.loreal.com',
      category: 'Beauty and Personal Care',
    },
    {
      name: 'Estée Lauder',
      url: 'www.esteelauder.com',
      category: 'Beauty and Personal Care',
    },
    {
      name: 'Bath & Body Works',
      url: 'www.bathandbodyworks.com',
      category: 'Beauty and Personal Care',
    },
    {
      name: 'The Body Shop',
      url: 'www.thebodyshop.com',
      category: 'Beauty and Personal Care',
    },
    {
      name: 'Glossier',
      url: 'www.glossier.com',
      category: 'Beauty and Personal Care',
    },
    {
      name: "Kiehl's",
      url: 'www.kiehls.com',
      category: 'Beauty and Personal Care',
    },
    {
      name: 'Lush',
      url: 'www.lush.com',
      category: 'Beauty and Personal Care',
    },
    {
      name: 'Nordstrom',
      url: 'www.nordstrom.com',
      category: 'Department Stores',
    },
    {
      name: "Macy's",
      url: 'www.macys.com',
      category: 'Department Stores',
    },
    {
      name: "Bloomingdale's",
      url: 'www.bloomingdales.com',
      category: 'Department Stores',
    },
    {
      name: 'Harrods',
      url: 'www.harrods.com',
      category: 'Department Stores',
    },
    {
      name: 'Selfridges',
      url: 'www.selfridges.com',
      category: 'Department Stores',
    },
    {
      name: 'Galeries Lafayette',
      url: 'www.galerieslafayette.com',
      category: 'Department Stores',
    },
    {
      name: 'El Corte Inglés',
      url: 'www.elcorteingles.es',
      category: 'Department Stores',
    },
    {
      name: 'John Lewis',
      url: 'www.johnlewis.com',
      category: 'Department Stores',
    },
    {
      name: 'Marks & Spencer',
      url: 'www.marksandspencer.com',
      category: 'Department Stores',
    },
    {
      name: 'Harvey Nichols',
      url: 'www.harveynichols.com',
      category: 'Department Stores',
    },
    {
      name: 'Home Depot',
      url: 'www.homedepot.com',
      category: 'DIY and Home Improvement',
    },
    {
      name: "Lowe's",
      url: 'www.lowes.com',
      category: 'DIY and Home Improvement',
    },
    {
      name: 'B&Q',
      url: 'www.diy.com',
      category: 'DIY and Home Improvement',
    },
    {
      name: 'Wickes',
      url: 'www.wickes.co.uk',
      category: 'DIY and Home Improvement',
    },
    {
      name: 'Ace Hardware',
      url: 'www.acehardware.com',
      category: 'DIY and Home Improvement',
    },
    {
      name: 'Menards',
      url: 'www.menards.com',
      category: 'DIY and Home Improvement',
    },
    {
      name: 'Bunnings',
      url: 'www.bunnings.com.au',
      category: 'DIY and Home Improvement',
    },
    {
      name: 'Leroy Merlin',
      url: 'www.leroymerlin.fr',
      category: 'DIY and Home Improvement',
    },
    {
      name: 'Hornbach',
      url: 'www.hornbach.com',
      category: 'DIY and Home Improvement',
    },
    {
      name: 'OBI',
      url: 'www.obi.com',
      category: 'DIY and Home Improvement',
    },
    {
      name: 'Saks Fifth Avenue',
      url: 'www.saksfifthavenue.com',
      category: 'Luxury Department Stores',
    },
    {
      name: 'Neiman Marcus',
      url: 'www.neimanmarcus.com',
      category: 'Luxury Department Stores',
    },
    {
      name: 'Bergdorf Goodman',
      url: 'www.bergdorfgoodman.com',
      category: 'Luxury Department Stores',
    },
    {
      name: 'Liberty London',
      url: 'www.libertylondon.com',
      category: 'Luxury Department Stores',
    },
    {
      name: 'Lane Crawford',
      url: 'www.lanecrawford.com',
      category: 'Luxury Department Stores',
    },
  ]

  return prospects[Math.floor(Math.random() * prospects.length)]
}
